import { LoadingSpinner } from "components/LoadingSpinner";
import { UserRole, userRoleMapping } from "constants/userRole";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { parseDate } from "helpers/date";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteUserMutation,
  useEnableUserMutation,
  useGetAllUsersQuery,
} from "store/queries/users";
import styled from "styled-components";
import { IUser, IUserResponse } from "types/User";

// CLE-3003: login status for the Last login column, read from the UserLoggedIn audit
// rows the API returns as lastLoginAt (ISO = last login, null = never, absent = the
// lookup failed).
type LoginStatus = "loggedIn" | "never" | "unknown";

const LOGIN_STATUS_OPTIONS = [
  { label: "Never logged in", value: "never" },
  { label: "Logged in", value: "loggedIn" },
];

const LOGIN_HISTORY_NOTE =
  "Last completed login (after the email / SMS code). Login history is recorded since March 2024.";

function getLoginStatus(
  lastLoginAt: IUserResponse["lastLoginAt"]
): LoginStatus {
  if (lastLoginAt === undefined) return "unknown";
  return lastLoginAt === null ? "never" : "loggedIn";
}

// Sort by the real timestamp. Never-logged-in users sort as older than any login (so
// they group together at the end of a newest-first sort), and "unknown" below them.
function getLoginSortKey(lastLoginAt: IUserResponse["lastLoginAt"]) {
  if (lastLoginAt === undefined) return -1;
  return lastLoginAt === null ? 0 : new Date(lastLoginAt).getTime();
}

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
`;

export function UsersTable() {
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();

  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const { current: toastElement } = useContext(ToastContext);

  const { data, isFetching, refetch } = useGetAllUsersQuery(null, {
    refetchOnMountOrArgChange: true,
  });
  const [deleteMutation] = useDeleteUserMutation();
  const [enableMutation] = useEnableUserMutation();

  const usersInfo = useMemo(() => {
    const users = data?.data;

    return (
      users?.map((user) => ({
        ...user,
        scope: userRoleMapping[user.scope as UserRole],
        createdAt: parseDate(user.createdAt, false, true),
        loginStatus: getLoginStatus(user.lastLoginAt),
        loginSortKey: getLoginSortKey(user.lastLoginAt),
      })) || []
    );
  }, [data]);

  const handleOnClickDeleteUsers = async () => {
    setIsLoading(true);

    try {
      const deletePromises = selectedUsers.map((user) =>
        deleteMutation(user.id)
      );

      await Promise.all(deletePromises);
      await refetch();

      toastElement?.show({
        severity: "success",
        summary: "Deleted!",
        detail: "User(s) has been deleted successfully.",
      });
    } catch (error) {
      await refetch();

      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "An error occurred while deleting users. Please try again.",
      });
    } finally {
      setIsLoading(false);
      setSelectedUsers([]);
    }
  };

  const userScopeRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        placeholder="Select a Role"
        className="p-column-filter"
        options={Object.values(userRoleMapping)}
        onChange={(e) => options.filterApplyCallback(e.value)}
      />
    );
  };

  const loginStatusRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        placeholder="Any login status"
        className="p-column-filter"
        options={LOGIN_STATUS_OPTIONS}
        showClear
        onChange={(e) => options.filterApplyCallback(e.value)}
      />
    );
  };

  const lastLoginBody = (row: IUserResponse & { loginStatus: LoginStatus }) => {
    if (row.loginStatus === "never") {
      return <Tag severity="warning" value="Never logged in" />;
    }
    if (row.loginStatus === "unknown" || !row.lastLoginAt) {
      return <span className="text-500">Unknown</span>;
    }
    return parseDate(row.lastLoginAt, false, true, "MM/DD/YYYY h:mm A");
  };

  // CLE-2765: disabled users (inactivity review or by hand) are tagged and can be
  // enabled again in one click.
  const nameBody = (row: IUserResponse) => (
    <span className="flex align-items-center gap-2">
      {row.name}
      {row.disabledAt && <Tag severity="danger" value="Disabled" />}
    </span>
  );

  const handleEnable = async (row: IUserResponse) => {
    try {
      await enableMutation(row.id).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Enabled",
        detail: `${row.name} can log in again and is off the review list for 90 days.`,
      });
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "The user could not be enabled. Please try again.",
      });
    }
  };

  const editUser = (row: IUserResponse) => {
    return (
      <div className="flex align-items-center">
        {row.disabledAt && (
          <Button className="p-button-link" onClick={() => handleEnable(row)}>
            <p className="small-text">Enable</p>
          </Button>
        )}
        <Button
          className="p-button-link"
          onClick={() => navigate(`/users/edit/${row.id}`)}
        >
          <p className="small-text">Edit</p>
        </Button>
      </div>
    );
  };

  if (isFetching) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="flex justify-content-end mb-3">
        <StyledButton
          icon="pi pi-plus"
          className="p-button-raised"
          onClick={() => navigate("/users/new")}
        >
          New User
        </StyledButton>
      </div>
      <DataTable
        rows={10}
        paginator
        stripedRows
        value={usersInfo}
        className="p-fluid"
        filterDisplay="row"
        responsiveLayout="scroll"
        selection={selectedUsers}
        emptyMessage="No users to show."
        onSelectionChange={(event) => setSelectedUsers(event.value)}
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />

        <Column
          filter
          field="name"
          header="Name"
          filterPlaceholder="John Smith"
          body={nameBody}
        />
        <Column
          filter
          field="email"
          header="Email"
          // CLE-3003: "contains" (the default is "startsWith") so typing a domain
          // such as cmciks.com finds every user at it.
          filterMatchMode="contains"
          filterPlaceholder="john.smith@clearhealthinc.com"
        />
        <Column
          filter
          field="scope"
          header="Scope"
          filterElement={userScopeRowFilterTemplate}
        />
        <Column
          filter
          field="createdAt"
          header="Created At"
          filterPlaceholder="mm/dd/yyyy"
        />
        <Column
          filter
          sortable
          field="loginStatus"
          sortField="loginSortKey"
          filterMatchMode="equals"
          showFilterMenu={false}
          header={
            <span title={LOGIN_HISTORY_NOTE}>
              Last login <i className="pi pi-info-circle text-xs" />
            </span>
          }
          body={lastLoginBody}
          filterElement={loginStatusRowFilterTemplate}
        />

        <Column field="editUser" body={(row) => editUser(row)} />
      </DataTable>
      <div className="flex justify-content-end">
        <Button
          label="Delete"
          disabled={!selectedUsers.length}
          className="p-button-outlined mt-4"
          onClick={() => {
            confirmDialog({
              header: "Confirmation",
              icon: "pi pi-exclamation-triangle",
              message: `Are you sure you want delete?`,
              accept: handleOnClickDeleteUsers,
            });
          }}
        />
      </div>
    </>
  );
}
